import React, { useMemo } from 'react';
import { __, always, gte, ifElse, map, min, pipe, prop, sum, isNil } from 'ramda';
import { Arc, Pie } from '@vx/shape';
import { Group } from '@vx/group';
import { ParentSize } from '@vx/responsive';

import { useIntl } from 'gatsby-plugin-intl';
import { renderWhenTrue } from '../../../utils/rendering';
import { NoData, isNoData } from '../noData';
import { Header } from '../header';
import { PIE_DEFAULT } from '../configs/pie.config';
import { Container, Chart, Svg } from '../charts.styles';
import { DEFAULT_SIZE } from '../charts.constants';
import { PieLegend } from './pieLegend.component';
import { Label, OutlinePath } from './pieChart.styles';
import { START_ANGLE, END_ANGLE, START, END } from './pieChart.constants';
import { useChartTooltipContext } from '../../../store/ChartTooltipContext';
import { renderOpenTooltip } from '../tooltipBasic/tooltipBasic.utils';
import { TooltipWithPortal } from '../tooltipWithPortal/tooltipWithPortal.component';
import { useTooltipInPortal } from '@vx/tooltip';
import { TooltipElementTable, TooltipTableDataCell } from '../../tooltipElementTable/tooltipElementTable.styles';

const renderLegend = ({ config, ...props }) =>
  renderWhenTrue(() => <PieLegend config={config} {...props} />)(config.legendVisibility);

const getTextAnchor = ifElse(gte(__, 0), always(START), always(END));

const renderPie = (colors) => (pie) => {
  const total = useMemo(() => sum(pie.arcs.map((arc) => arc.value)), []);
  const tooltip = useChartTooltipContext();

  const getPercent = (value) => `${Math.round((value / total) * 100)}%`;

  const handleHover = (e, arc, index) => {
    const rect = e.target.getBoundingClientRect();
    const left = rect.left;
    const top = rect.top;

    if ((left === tooltip.left && top === tooltip.top) || isNil(arc.data)) {
      return;
    }

    tooltip.show({
      data: {
        ...arc.data,
        percent: getPercent(arc.value),
        color: colors[index],
      },
      index: index,
      top: top,
      left: left,
    });
  };

  return pie.arcs.map((arc, index) => {
    return (
      <path
        key={index}
        d={pie.path(arc)}
        fill={colors[index]}
        {...tooltip.bindHoverEvents((e) => handleHover(e, arc, index))}
      />
    );
  });
};

const renderLabel = ({ total, serie, label }) => (pie) =>
  pie.arcs.map((arc, index) => {
    const value = Math.round((prop(serie, arc.data) / total) * 100);
    const [centroidX, centroidY] = pie.path.centroid(arc);
    const textAnchor = getTextAnchor(centroidX);

    return (
      <Label key={index} textAnchor={textAnchor} transform={`translate(${centroidX}, ${centroidY})`}>
        <tspan x={0}>{prop(label, arc.data)}</tspan>
        <tspan x={0} dy={'1em'}>
          {value}%
        </tspan>
      </Label>
    );
  });

const renderOutline = ({ innerRadius, outerRadius }, { pieOutlineWidth }) => (
  <Arc
    outerRadius={outerRadius + pieOutlineWidth}
    innerRadius={innerRadius - pieOutlineWidth}
    startAngle={START_ANGLE}
    endAngle={END_ANGLE}
  >
    {(arc) => <OutlinePath d={arc.path(arc)} />}
  </Arc>
);

export const PieChart = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  data: chartData = {},
  tabs,
  parser: customParser,
  config: customConfig = {},
}) => {
  if (customParser) customParser(chartData);
  const intl = useIntl();
  chartData['title']=intl.formatMessage({ id: chartData.title })

  const { title, subtitle, data, label, serie } = chartData;
  if (isNoData(data, serie, label)) return <NoData width={width} height={height} />;

  const config = {
    ...PIE_DEFAULT,
    ...customConfig,
  };

  const total = pipe(map(prop(serie)), sum)(data);
  const tooltip = useChartTooltipContext();
  const { containerRef } = useTooltipInPortal();

  const renderTooltipContent = () => {
    if (isNil(tooltip.data)) {
      return null;
    }

    const categoryKey = Object.keys(tooltip.data)[0];
    const VALUE_COLUMN_NAME = 'percent';

    return (
      <TooltipElementTable>
        <thead>
          <tr>
            <th>{categoryKey}</th>
            <th>{VALUE_COLUMN_NAME}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <TooltipTableDataCell>{tooltip.data[categoryKey]}</TooltipTableDataCell>
            <TooltipTableDataCell color={tooltip.data.color}>
              <span>{tooltip.data[VALUE_COLUMN_NAME]}</span>
            </TooltipTableDataCell>
          </tr>
        </tbody>
      </TooltipElementTable>
    );
  };
  return (
    <Container width={width} height={height}>
      <Header title={title} subtitle={subtitle} tabs={tabs} config={config}>
        {renderLegend({ label, data, config })}
      </Header>
      <Chart ref={containerRef}>
        {renderOpenTooltip(always(<TooltipWithPortal>{renderTooltipContent()}</TooltipWithPortal>), tooltip)}

        <ParentSize>
          {({ width: chartWidth, height: chartHeight }) => {
            if (!chartWidth || !chartHeight) return null;

            const radius = min(chartWidth, chartHeight) / 2;
            const centerY = chartHeight / 2;
            const centerX = chartWidth / 2;

            const outerRadius = radius * config.pieOuterRadius;
            const innerRadius = radius * config.pieInnerRadius;
            const labelOuterRadius = radius * config.labelOuterRadius;

            return (
              <Svg>
                <rect width={chartWidth} height={chartHeight} fill={config.background} />
                <Group top={centerY} left={centerX}>
                  {renderOutline({ innerRadius, outerRadius }, config)}
                  <Group>
                    <Pie
                      data={data}
                      pieValue={prop(serie)}
                      outerRadius={outerRadius}
                      innerRadius={innerRadius}
                      padAngle={START_ANGLE}
                    >
                      {renderPie(config.colors)}
                    </Pie>
                  </Group>
                  <Group>
                    <Pie
                      data={data}
                      pieValue={prop(serie)}
                      outerRadius={labelOuterRadius}
                      innerRadius={outerRadius}
                      padAngle={START_ANGLE}
                    >
                      {renderLabel({
                        total,
                        serie,
                        label,
                      })}
                    </Pie>
                  </Group>
                </Group>
              </Svg>
            );
          }}
        </ParentSize>
      </Chart>
    </Container>
  );
};
